import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import getOnlyUserRUC from "./getOnlyUserRUC";

/**
 * Exportador del reporte PDF de envío / recepción de documentos laborales.
 *
 * Contrato público (estable):
 *   exportarToPDF(data, rucEmpresa) -> Promise<Uint8Array>
 *
 * Consumidores: Usuarios, DescargarDocumentosGestion, SeguimientoOnboarding,
 * GestionMedica y DescargarDocumentos (Onboarding).
 */

const ZONA_HORARIA = "America/Lima";
const VALOR_VACIO = "—";

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "setiembre", "octubre", "noviembre", "diciembre",
];

// ───────────────────────────────────────────────────────────────────────────
// Utilidades puras (exportadas para pruebas unitarias)
// ───────────────────────────────────────────────────────────────────────────

/** Convierte un valor a texto sin producir nunca "undefined" / "null". */
export function textoSeguro(valor) {
  if (valor === null || valor === undefined) return "";
  if (typeof valor === "string") return valor.trim();
  if (typeof valor === "number") return Number.isFinite(valor) ? String(valor) : "";
  if (typeof valor === "boolean") return valor ? "SI" : "NO";
  return "";
}

/** Devuelve el valor si es "usable", o el texto alternativo. */
export function valorONo(valor, alternativo = VALOR_VACIO) {
  const texto = textoSeguro(valor);
  return texto.length > 0 ? texto : alternativo;
}

/**
 * Indica si un campo tiene información, sea texto, número u objeto
 * (por ejemplo un Timestamp de Firestore). A diferencia de `textoSeguro`,
 * no descarta los objetos.
 */
export function tieneValor(valor) {
  if (valor === null || valor === undefined) return false;
  if (typeof valor === "string") return valor.trim().length > 0;
  if (typeof valor === "number") return Number.isFinite(valor);
  if (typeof valor === "boolean") return valor;
  if (typeof valor === "object") return true;
  return Boolean(valor);
}

/** Normaliza un Timestamp / Date / número / string a "DD-MM-YYYY HH:mm". */
export function formatearFecha(valor) {
  if (valor === null || valor === undefined || valor === "") return "";

  if (valor instanceof Date) {
    return esFechaValida(valor) ? componerFecha(valor) : "";
  }

  if (typeof valor === "object") {
    // Firestore Timestamp (instancia)
    if (typeof valor.toDate === "function") {
      const fecha = valor.toDate();
      return esFechaValida(fecha) ? componerFecha(fecha) : "";
    }
    // Firestore Timestamp (objeto plano { seconds, nanoseconds })
    if (typeof valor.seconds === "number") {
      const fecha = new Date(valor.seconds * 1000);
      return esFechaValida(fecha) ? componerFecha(fecha) : "";
    }
    return "";
  }

  if (typeof valor === "number") {
    if (!Number.isFinite(valor)) return "";
    // Heurística: los timestamps en segundos son < 10 000 000 000
    const milisegundos = valor < 10000000000 ? valor * 1000 : valor;
    const fecha = new Date(milisegundos);
    return esFechaValida(fecha) ? componerFecha(fecha) : "";
  }

  if (typeof valor !== "string") return "";

  const texto = valor.trim();
  if (!texto) return "";

  // Formato del sistema: "22-09-2024,22:23:037" / "15-03-2026,10:30" / "15-03-2026"
  const formatoIso = texto.match(/^(\d{2})-(\d{2})-(\d{4})(?:[,\s]+(\d{1,2}):(\d{2}))?/);
  if (formatoIso) {
    const [, dia, mes, anio, hora, minuto] = formatoIso;
    return `${dia}-${mes}-${anio}${hora ? ` ${hora.padStart(2, "0")}:${minuto}` : ""}`;
  }

  // Formato ISO: "2026-01-15" / "2026-01-15T10:30:00Z"
  const formatoAnioPrimero = texto.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]+(\d{1,2}):(\d{2}))?/);
  if (formatoAnioPrimero) {
    const [, anio, mes, dia, hora, minuto] = formatoAnioPrimero;
    return `${dia}-${mes}-${anio}${hora ? ` ${hora.padStart(2, "0")}:${minuto}` : ""}`;
  }

  // Último recurso: parseo nativo
  const fecha = new Date(texto);
  return esFechaValida(fecha) ? componerFecha(fecha) : "";
}

function esFechaValida(fecha) {
  return fecha instanceof Date && !Number.isNaN(fecha.getTime());
}

function componerFecha(fecha) {
  const partes = new Intl.DateTimeFormat("es-PE", {
    timeZone: ZONA_HORARIA,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    hourCycle: "h23",
  }).formatToParts(fecha);

  const valor = (tipo) =>
    (partes.find((parte) => parte.type === tipo)?.value || "").padStart(2, "0");

  return `${valor("day")}-${valor("month")}-${valor("year")} ${valor("hour")}:${valor("minute")}`;
}

/** Periodo legible: "MM/AAAA", "MM", "AAAA" o "—". Nunca "undefined". */
export function formatearPeriodo(periodo, ano) {
  const mesTexto = textoSeguro(periodo);
  const anioTexto = textoSeguro(ano);

  let mes = mesTexto;
  if (mes && /^\d{1,2}$/.test(mes)) {
    const numeroMes = Number(mes);
    mes = numeroMes >= 1 && numeroMes <= 12 ? numeroMes.toString().padStart(2, "0") : "";
  }

  if (mes && anioTexto) return `${mes}/${anioTexto}`;
  if (mes) return mes;
  if (anioTexto) return anioTexto;
  return VALOR_VACIO;
}

/** ¿El documento fue visto por el colaborador? */
export function esVisto(registro) {
  return (
    (typeof registro.contador === "number" && registro.contador > 0) ||
    tieneValor(registro.fechaVista) ||
    tieneValor(registro.linkFirmado)
  );
}

/** ¿El documento fue firmado? */
export function esFirmado(registro) {
  return (
    tieneValor(registro.linkFirmado) ||
    tieneValor(registro.fechaFirma) ||
    String(registro.estadoEnLegajo || "").trim().toLowerCase() === "firmado"
  );
}

/**
 * ¿Existe evidencia de envío?
 *   - `enviado === true`  → enviado (marca explícita)
 *   - `enviado === false` → no enviado (marca explícita)
 *   - sin marca            → se infiere del registro del documento
 */
export function estadoEnvio(registro) {
  if (registro.enviado === true) return "SI";
  if (registro.enviado === false) return "NO";
  if (tieneValor(registro.fechaEnvio) || tieneValor(registro.fechaDoc)) return "SI";
  return VALOR_VACIO;
}

/** Dispositivo real de interacción (no se infiere de la firma). */
export function dispositivo(registro) {
  const plataforma = textoSeguro(registro.plataforma);
  if (plataforma) return plataforma;
  return VALOR_VACIO;
}

/**
 * Normaliza los registros de cualquier origen al esquema del reporte.
 * Tolera campos ausentes y nunca deja "undefined" en la salida.
 */
export function normalizarRegistros(data) {
  if (!Array.isArray(data)) return [];

  return data.map((registro, indice) => {
    const origen = registro || {};

    const fechaEnvio =
      formatearFecha(origen.fechaEnvio) || formatearFecha(origen.fechaDoc);

    const fechaFirma = formatearFecha(origen.fechaFirma);
    const fechaVista = formatearFecha(origen.fechaVista);
    const fechaRecepcion = fechaFirma || fechaVista;

    return {
      item: indice + 1,
      dni: valorONo(origen.dni),
      colaborador: valorONo(origen.colaborador),
      tipo: valorONo(origen.tipo),
      periodo: formatearPeriodo(
        origen.periodo !== undefined && origen.periodo !== null ? origen.periodo : origen.mes,
        origen.ano
      ),
      fechaEnvio,
      email: valorONo(origen.email),
      enviado: estadoEnvio(origen),
      visto: esVisto(origen) ? "SI" : "NO",
      firmado: esFirmado(origen) ? "SI" : "NO",
      dispositivo: dispositivo(origen),
      fechaRecepcion,
      ruc: textoSeguro(origen.ruc),
    };
  });
}

/** Totales para el resumen ejecutivo de la cabecera. */
export function calcularResumen(registros) {
  const total = registros.length;
  const enviados = registros.filter((r) => r.enviado === "SI").length;
  const vistos = registros.filter((r) => r.visto === "SI").length;
  const firmados = registros.filter((r) => r.firmado === "SI").length;

  const porcentaje = (parte) => (total > 0 ? Math.round((parte / total) * 100) : 0);

  return {
    total,
    enviados,
    vistos,
    firmados,
    pctVisto: porcentaje(vistos),
    pctFirmado: porcentaje(firmados),
  };
}

/** Ordena por colaborador y luego por fecha de envío (estable, sin NaN). */
export function ordenarRegistros(registros) {
  return [...registros].sort((a, b) => {
    const nombreA = String(a.colaborador || "");
    const nombreB = String(b.colaborador || "");
    if (nombreA !== nombreB) return nombreA.localeCompare(nombreB, "es");

    const fechaA = a.fechaEnvio || "";
    const fechaB = b.fechaEnvio || "";
    if (fechaA !== fechaB) return fechaA < fechaB ? -1 : 1;
    return 0;
  });
}

// ───────────────────────────────────────────────────────────────────────────
// Exportador
// ───────────────────────────────────────────────────────────────────────────

// Los anchos se expresan como fracción del ancho útil de la página y se
// convierten a puntos en tiempo de ejecución. Deben sumar 1.
const COLUMNAS = [
  { header: "Item", key: "item", frac: 0.032, overflow: "linebreak" },
  { header: "Doc Identidad", key: "dni", frac: 0.062, overflow: "ellipsize" },
  { header: "Colaborador", key: "colaborador", frac: 0.18, overflow: "linebreak" },
  { header: "Documento", key: "tipo", frac: 0.1, overflow: "ellipsize" },
  { header: "Periodo", key: "periodo", frac: 0.052, overflow: "ellipsize" },
  { header: "Fecha de Envío", key: "fechaEnvio", frac: 0.09, overflow: "ellipsize" },
  { header: "Correo", key: "email", frac: 0.14, overflow: "ellipsize" },
  { header: "Enviado", key: "enviado", frac: 0.045, overflow: "ellipsize" },
  { header: "Visto", key: "visto", frac: 0.038, overflow: "ellipsize" },
  { header: "Firmado", key: "firmado", frac: 0.048, overflow: "ellipsize" },
  { header: "Dispositivo", key: "dispositivo", frac: 0.06, overflow: "ellipsize" },
  { header: "Fecha de Recepción", key: "fechaRecepcion", frac: 0.153, overflow: "ellipsize" },
];

const COLOR_ENCABEZADO = [30, 58, 95];
const COLOR_FILA_ALT = [245, 247, 250];

async function obtenerRazonSocial(ruc) {
  try {
    const registro = await getOnlyUserRUC("company", ruc);
    const nombre = textoSeguro(registro?.name) || textoSeguro(registro?.nombreComercial);
    return nombre || "";
  } catch (error) {
    console.warn("No se pudo obtener la razón social de la empresa:", error);
    return "";
  }
}

/**
 * Genera el PDF del reporte de envío / recepción.
 * @param {Array} data Registros normalizados o crudos (se normalizan aquí).
 * @param {string} rucEmpresa RUC de la empresa.
 * @returns {Promise<Uint8Array>} Bytes del PDF.
 */
export async function exportarToPDF(data, rucEmpresa) {
  const registros = ordenarRegistros(normalizarRegistros(data));
  const resumen = calcularResumen(registros);
  const ruc = textoSeguro(rucEmpresa);
  const razonSocial = await obtenerRazonSocial(ruc);

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const anchoPagina = doc.internal.pageSize.getWidth();
  const margen = 28;
  const anchoUtil = anchoPagina - margen * 2;

  // Anchos en puntos: la última columna absorbe el redondeo para que la
  // tabla ocupe exactamente el ancho útil (sin desbordes ni holgura).
  const anchosColumna = [];
  let anchoAcumulado = 0;
  COLUMNAS.forEach((columna, indice) => {
    if (indice === COLUMNAS.length - 1) {
      anchosColumna.push(Math.round(anchoUtil - anchoAcumulado));
    } else {
      const ancho = Math.round(anchoUtil * columna.frac);
      anchosColumna.push(ancho);
      anchoAcumulado += ancho;
    }
  });

  // ── Encabezado ───────────────────────────────────────────────────────────
  doc.setFillColor(...COLOR_ENCABEZADO);
  doc.rect(margen, margen, anchoPagina - margen * 2, 46, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("REPORTE DE ENVÍO Y RECEPCIÓN DE DOCUMENTOS", margen + 14, margen + 20);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const identidadEmpresa = [
    textoSeguro(razonSocial) || "Empresa no identificada",
    ruc ? `RUC: ${ruc}` : "",
  ]
    .filter(Boolean)
    .join("  ·  ");
  doc.text(identidadEmpresa, margen + 14, margen + 35);

  // ── Resumen ejecutivo ────────────────────────────────────────────────────
  const resumenTexto =
    `Total: ${resumen.total}   |   Enviados: ${resumen.enviados}` +
    `   |   Vistos: ${resumen.vistos} (${resumen.pctVisto}%)` +
    `   |   Firmados: ${resumen.firmados} (${resumen.pctFirmado}%)`;

  doc.setTextColor(40, 40, 40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(resumenTexto, margen, margen + 62);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(
    "Enviado: marca de envío registrada (o documento generado). Visto: el colaborador abrió el documento. " +
      "Firmado: el documento cuenta con firma o enlace firmado.",
    margen,
    margen + 76
  );

  // ── Tabla principal ──────────────────────────────────────────────────────
  const cuerpo = registros.map((registro) => [
    registro.item,
    registro.dni,
    registro.colaborador,
    registro.tipo,
    registro.periodo,
    registro.fechaEnvio,
    registro.email,
    registro.enviado,
    registro.visto,
    registro.firmado,
    registro.dispositivo,
    registro.fechaRecepcion,
  ]);

  autoTable(doc, {
    startY: margen + 86,
    head: [COLUMNAS.map((columna) => columna.header)],
    body: cuerpo.length > 0 ? cuerpo : [[VALOR_VACIO, VALOR_VACIO, "Sin registros", VALOR_VACIO, VALOR_VACIO, VALOR_VACIO, VALOR_VACIO, VALOR_VACIO, VALOR_VACIO, VALOR_VACIO, VALOR_VACIO, VALOR_VACIO]],
    margin: { left: margen, right: margen, bottom: 46 },
    styles: {
      font: "helvetica",
      fontSize: 7,
      cellPadding: 3,
      overflow: "linebreak",
      lineWidth: 0.5,
      lineColor: [200, 205, 212],
      textColor: [30, 30, 30],
      valign: "middle",
    },
    headStyles: {
      fillColor: COLOR_ENCABEZADO,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7,
      halign: "center",
      valign: "middle",
      cellPadding: 4,
    },
    alternateRowStyles: {
      fillColor: COLOR_FILA_ALT,
    },
    columnStyles: Object.fromEntries(
      anchosColumna.map((ancho, indice) => [
        indice,
        {
          cellWidth: ancho,
          overflow: COLUMNAS[indice].overflow,
          halign: ["item", "enviado", "visto", "firmado", "periodo"].includes(
            COLUMNAS[indice].key
          )
            ? "center"
            : "left",
        },
      ])
    ),
    didDrawPage: (data) => {
      const totalPaginas = doc.getNumberOfPages();
      const altoPagina = doc.internal.pageSize.getHeight();

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);

      doc.text(
        `Página ${data.pageNumber} de ${totalPaginas}`,
        anchoPagina / 2,
        altoPagina - 22,
        { align: "center" }
      );
      doc.text(
        `Reporte generado ${formatearFecha(new Date())}`,
        margen,
        altoPagina - 22
      );
    },
  });

  return new Uint8Array(doc.output("arraybuffer"));
}

export { MESES };
